import React, { useEffect, useState } from 'react';

import { Field, Formik } from 'formik';
import * as Yup from 'yup';

import { generateKey } from 'Helpers/misc';

import { EyeIcon } from 'CommonComponents/icons';

const IntegrationSaveSchema = Yup.object().shape({
    client_id: Yup.string().required('This value is required'),
    client_secret: Yup.string().required('This value is required'),
});

export default function IntegrationsSetupListItemSaveXeroForm(props) {
    const [showSecret, setShowSecret] = useState(false);
    const [step, setStep] = useState(props.initialStep || 1);
    const [submitDisabled, setSubmitDisabled] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [company, setCompany] = useState(props.integration?.config?.company ? props.integration.config.company : {});

    useEffect(() => {
        if (props.initialStep == 2) {
            const data = {
                client_id: props.integration ? props.integration.client_id : '',
                client_secret: props.integration ? props.integration.client_secret : '',
            };

            testConnection(data, null);
        }

        if (props.integration?.config?.company) {
            setCompanies([props.integration.config.company]);
        }
    }, []);

    useEffect(() => {
        const result = step == 2 ? Object.keys(company).length === 0 : false;

        setSubmitDisabled(result);
    }, [step, company, companies]);

    const changeCompany = (e) => {
        const id = e.target.value;

        const item = companies.find((c) => {
            return c.id === id;
        });
        const data = {
            id,
            name: item ? item.name : '',
            connectionId: item ? item.connectionId : '',
        };

        setCompany(data);
    };

    const testConnection = (data, actions) => {
        props.setError(null);
        props.setRequest(true);
        data.code = props.provider.code;
        data.provider_id = props.provider.id;
        data.config = [];

        axios
            .post(props.testConnectionUrl, data)
            .then((response) => {
                setCompanies(response.data.connection);
            })
            .catch((e) => {
                const err = e.response?.data?.message || e.response?.data?.error;
                if (err) {
                    props.setError(err);
                }
                props.setIntegrationCreated(false);
                props.setRequestFailed(true);
                if (actions) {
                    actions.setErrors(e.response.data.errors);
                }
            })
            .finally(() => {
                props.setRequest(false);
                if (actions) {
                    actions.setSubmitting(false);
                }
            });
    };

    const createOrUpdateIntegration = (values, actions) => {
        const data = {
            client_id: values.client_id,
            client_secret: values.client_secret,
        };
        axios
            .post(props.provider.pre_save_url, data)
            .then((res) => {
                const state = generateKey();
                const scopes = props.provider.options.scopes;

                let url = props.provider.options.auth_url;
                let params = new URLSearchParams();
                params.set('response_type', 'code');
                params.set('client_id', values.client_id);
                params.set('redirect_uri', props.provider.redirect_url);
                params.set('state', state);
                params.set('scope', scopes.join(' '));

                url = url + '?' + params.toString();
                window.location.href = url;
            })
            .catch((e) => {
                console.log(e.response);
            });
    };

    const updateIntegration = (data, actions) => {
        props.setError(null);
        props.setRequest(true);
        const url = props.updateUrl.replace('INTEGRATION_ID', props.provider.integration.id);
        data.config = {
            company: company,
        };

        axios
            .put(url, data)
            .then((response) => {
                props.setIntegrationCreated(true);
                window.location.href = window.location.origin + window.location.pathname;
            })
            .catch((e) => {
                const err = e.response?.data?.message || e.response?.data?.error;
                if (err) {
                    props.setError(err);
                }
                props.setIntegrationCreated(false);
                props.setRequestFailed(true);
                if (actions) {
                    actions.setErrors(e?.response?.data?.errors);
                }
            })
            .finally(() => {
                props.setRequest(false);
                if (actions) {
                    actions.setSubmitting(false);
                }
            });
    };

    return (
        <Formik
            initialValues={{
                client_id: props.integration ? props.integration.raw_client_id : '',
                client_secret: props.integration ? props.integration.raw_client_secret : '',
            }}
            validationSchema={IntegrationSaveSchema}
            onSubmit={(values, actions) => {
                if (step === 1) {
                    createOrUpdateIntegration(values, actions);
                }
                if (step == 2) {
                    updateIntegration(values, actions);
                }
            }}
        >
            {({ values, errors, touched, isSubmitting, handleSubmit, setFieldValue }) => (
                <>
                    <div className="tw-flex tw-flex-row tw-my-3 tw-items-center tw-text-left">
                        <label htmlFor="client_id" className="tw-basis-3/12 tw-font-bold">
                            {$t('Redirect Url')}
                        </label>
                        <div className="tw-basis-9/12">
                            <input
                                type="text"
                                className={'input text-input'}
                                name="redirect_url"
                                placeholder={$t('Redirect Url')}
                                value={props.provider.redirect_url}
                                onChange={() => {}}
                                disabled={true}
                            />
                        </div>
                    </div>

                    <div className="tw-flex tw-flex-row tw-my-3 tw-items-center tw-text-left">
                        <label htmlFor="client_id" className="tw-basis-3/12 tw-font-bold">
                            {$t('App Id')} <span className="tw-text-red-500">*</span>
                        </label>
                        <div className="tw-basis-9/12">
                            <Field
                                type="text"
                                className={errors && errors.client_id && touched.client_id ? 'input text-input invalid' : 'input text-input'}
                                name="client_id"
                                placeholder={$t('App Id')}
                            />
                            {errors && errors.client_id && touched.client_id && <div className="tw-text-red-500">{errors.client_id}</div>}
                        </div>
                    </div>

                    <div className="tw-flex tw-flex-row tw-my-3 tw-items-center tw-text-left">
                        <label htmlFor="client_secret" className="tw-basis-3/12 tw-font-bold">
                            {$t('App Secret')} <span className="tw-text-red-500">*</span>
                        </label>
                        <div className="tw-basis-9/12 tw-relative">
                            <Field
                                type={showSecret ? 'text' : 'password'}
                                className={errors && errors.client_secret && touched.client_secret ? 'input text-input invalid' : 'input text-input'}
                                name="client_secret"
                                placeholder={$t('App Secret')}
                            />
                            <span className="tw-absolute tw-cursor-pointer" style={{ top: '35%', right: '1%' }} onClick={(e) => setShowSecret(!showSecret)}>
                                <EyeIcon className="tw-w-3 tw-h-3" />
                            </span>
                            {errors && errors.client_secret && touched.client_secret && <div className="tw-text-red-500">{errors.client_secret}</div>}
                        </div>
                    </div>

                    {(step === 2 || props.action === 'update') && (
                        <>
                            <div className="tw-flex tw-flex-row tw-my-3 tw-items-center tw-text-left">
                                <label htmlFor="company" className="tw-basis-3/12 tw-font-bold">
                                    {$t('Company')} <span className="tw-text-red-500">*</span>
                                </label>
                                <div className="tw-basis-9/12">
                                    <select
                                        className="select select-input"
                                        value={company ? company.id : ''}
                                        onChange={changeCompany}
                                        disabled={!!props.integration?.config?.company}
                                    >
                                        {Object.keys(company).length === 0 && <option value={''}>{$t('Choose company')}</option>}
                                        {companies.map((item) => {
                                            return (
                                                <option key={item.id} value={item.id}>
                                                    {item.name}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                            </div>
                        </>
                    )}

                    <div className="modal-footer">
                        {props.error && <div className="tw-text-red-500 tw-py-2">{props.error}</div>}
                        <button type="button" className="button danger" onClick={props.internalCloseHandler} disabled={props.request}>
                            {$t('Cancel')}
                        </button>
                        <button type="button" className="button primary" onClick={handleSubmit} disabled={props.request || submitDisabled}>
                            {props.request && <i className="fa fa-spinner fa-spin " aria-hidden="true"></i>}
                            {step === 1 && props.action === 'create' ? $t('Create') : $t('Save')}
                        </button>
                    </div>
                </>
            )}
        </Formik>
    );
}
